import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'

import type { CommonResponse } from 'types/api'
import type { PelaksanaanError } from 'types/kinerja-ks-pelaksanaan'
import type {
  TPayloadPenilaian,
  TPayloadPenilaianForDelivery,
  TPenilaianFilterStatus,
  TPenilaianTujuanState,
  TResponsePenilaian,
  TResponsePenilaianCount,
  TResponsePenilaianForDelivery,
  TResponsePenilaianSummary,
} from 'types/kinerja-dinas'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

export const API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_COUNT_BY_DINAS_QUERY_KEY =
  'API_GET_KS_PENILAIAN_COUNT_BY_DINAS_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_QUERY_KEY'

type TPenilaianSummaryDinasProp = {
  state?: TPenilaianTujuanState
}

type TPenilaianDinasProp = TPenilaianSummaryDinasProp & {
  name?: string
  status?: TPenilaianFilterStatus[]
  confirmedOnly?: boolean
}

export function useApiGetPenilaianDinas(
  params: TPenilaianDinasProp,
  opts?: UseQueryOptions<TResponsePenilaian>
) {
  const nameParam = params?.name || undefined

  const status: TPenilaianFilterStatus[] = params?.status || ['ALL']
  const statusParam = status ? status?.join(',') : undefined

  const stateParam = params?.state || undefined

  // Intentionally exclude `false` from the query string so the API can handle it according to its default behavior
  const confirmedOnlyParam = params?.confirmedOnly === true || undefined

  return useKinerjaApi<TResponsePenilaian>(apiPaths.penilaianDinas, {
    axiosConfig: {
      params: {
        name: nameParam,
        statusPengiriman: statusParam,
        state: stateParam,
        confirmedOnly: confirmedOnlyParam,
      },
    },
    queryConfig: {
      useErrorBoundary: false,
      ...opts,
    },
    queryKey: [
      API_GET_KS_PENILAIAN_BY_DINAS_QUERY_KEY,
      nameParam,
      statusParam,
      stateParam,
      confirmedOnlyParam,
    ],
  })
}

export function useApiGetPenilaianCountDinas(
  opts?: UseQueryOptions<TResponsePenilaianCount>
) {
  return useKinerjaApi<TResponsePenilaianCount>(
    `${apiPaths.penilaianDinas}/count`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_DINAS_QUERY_KEY],
    }
  )
}

export function useApiGetPenilaianSummaryDinas(
  params: TPenilaianSummaryDinasProp,
  opts?: UseQueryOptions<TResponsePenilaianSummary>
) {
  const stateParam = params?.state || undefined

  return useKinerjaApi<TResponsePenilaianSummary>(
    `${apiPaths.penilaianDinas}/summary`,
    {
      axiosConfig: {
        params: {
          state: stateParam,
        },
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_BY_DINAS_SUMMARY_QUERY_KEY, stateParam],
    }
  )
}

export function useApiPostPenilaianDinasForDelivery(
  opts?: UseMutationOptions<
    CommonResponse<TResponsePenilaianForDelivery>,
    PelaksanaanError,
    TPayloadPenilaianForDelivery
  >
) {
  return useKinerjaMutationApi(apiPaths.penilaianDinas, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

export function useApiPatchPenilaianDinas(
  opts?: UseMutationOptions<
    CommonResponse<any>,
    PelaksanaanError,
    TPayloadPenilaian
  >
) {
  return useKinerjaMutationApi(`${apiPaths.penilaianDinas}/override`, {
    axiosConfig: {
      method: 'PATCH',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}
